import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { getApiUrl } from '../config/api.config';

const NDA_DOCUMENT = (
    <div className="text-sm text-gray-700 space-y-4">
        <h3 className="font-semibold text-gray-900">NON-DISCLOSURE AGREEMENT (NDA)</h3>
        <p className="text-gray-600">This Non-Disclosure Agreement (&quot;Agreement&quot;) is entered into as of the date of your acceptance between you (&quot;Recipient&quot;) and iVault / Techpuram (&quot;Disclosing Party&quot;) in connection with your use of the iVault platform, including any confidential information you may access, disclose, or receive through the platform.</p>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">1. Definition of Confidential Information</h4>
            <p>&quot;Confidential Information&quot; means any non-public information, in any form, disclosed by the Disclosing Party or any user of iVault, or accessed by Recipient through the iVault platform, including but not limited to: business plans, financial statements, projections, cap tables, term sheets, pitch decks, data room documents, investor lists, startup details, due diligence materials, technical and product information, customer and partner information, strategic plans, and any other information designated as confidential or that a reasonable person would understand to be confidential given the nature of the information and the circumstances of disclosure.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">2. Obligations of Recipient</h4>
            <p>Recipient agrees to: (a) hold all Confidential Information in strict confidence and not disclose it to any third party without the prior written consent of the Disclosing Party or the party that disclosed such information; (b) use Confidential Information solely for the purpose of evaluating or participating in investment opportunities, business relationships, or other lawful purposes as permitted by iVault and the disclosing party; (c) protect Confidential Information using at least the same degree of care used to protect Recipient&apos;s own confidential information, but in no event less than reasonable care; (d) not copy, reproduce, or distribute Confidential Information except as necessary for the permitted use; (e) limit access to Confidential Information to Recipient&apos;s employees, advisors, or agents who have a need to know and who are bound by confidentiality obligations no less restrictive than those in this Agreement.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">3. Exclusions</h4>
            <p>Confidential Information does not include information that: (a) is or becomes publicly available through no fault of Recipient; (b) was rightfully in Recipient&apos;s possession without restriction prior to disclosure; (c) is independently developed by Recipient without use of or reference to the Confidential Information; (d) is rightfully received from a third party without breach of any obligation of confidentiality. The burden of establishing that information falls within these exclusions shall be on Recipient.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">4. Compelled Disclosure</h4>
            <p>If Recipient is required by law or legal process to disclose any Confidential Information, Recipient shall give the Disclosing Party prompt notice (where permitted by law) so that the Disclosing Party may seek a protective order or other remedy. Recipient shall cooperate in such efforts and shall disclose only that portion of the Confidential Information that is legally required.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">5. Term and Survival</h4>
            <p>This Agreement is effective upon your acceptance and remains in effect for so long as you have access to iVault or any Confidential Information. Your obligations with respect to Confidential Information shall survive for five (5) years after your last access to such information or the termination of your account, whichever is later.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">6. Return or Destruction of Materials</h4>
            <p>Upon the request of the Disclosing Party or upon termination of your access to iVault, you will promptly cease use of and return or destroy all Confidential Information (including copies and derivatives) in your possession or control. Upon request, you will certify in writing that you have complied with this obligation.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">7. No License or Warranty</h4>
            <p>No license or right under any intellectual property right is granted by this Agreement. Confidential Information is provided &quot;as is&quot; and the Disclosing Party makes no warranty regarding its accuracy or completeness. Recipient assumes all risk in using or relying on Confidential Information.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">8. Remedies</h4>
            <p>Recipient acknowledges that breach of this Agreement may cause irreparable harm for which monetary damages may be inadequate. The Disclosing Party shall be entitled to seek equitable relief (including injunction and specific performance) in addition to any other remedies available at law or in equity.</p>
        </section>

        <section>
            <h4 className="font-medium text-gray-900 mt-2 mb-1">9. General</h4>
            <p>This Agreement constitutes the entire agreement between the parties regarding confidentiality of information accessed through iVault. It may not be amended except in writing. If any provision is held unenforceable, the remaining provisions remain in effect. Your acceptance of this Agreement by checking the box below constitutes your binding agreement and has the same effect as a handwritten signature.</p>
        </section>
    </div>
);

const USER_TYPES = [
    { value: 'startup', label: 'Startup', description: 'Raising funds, managing data room', icon: '🚀' },
    { value: 'investor', label: 'Investor', description: 'Individual angel or VC investor', icon: '👤' },
    { value: 'investor_network', label: 'Investor Network', description: 'VC firm, angel network, accelerator', icon: '🏢' }
];

/** Derive URL-safe identifier from company/organization name (used for app URL path, e.g. /acme-corp/dashboard) */
function slugifyTenantIdentifier(name) {
    if (!name || !name.trim()) return 'default';
    const slug = name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    return slug.length >= 3 ? slug : slug ? slug + '-org' : 'default';
}

const Signup = () => {
    const [userType, setUserType] = useState(null); // 'startup' | 'investor' | 'investor_network'
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        tenantName: '',
        ndaAgreed: false
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const isStartup = userType === 'startup';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setError('');

        if (!userType) {
            setError('Please select whether you are a Startup, Investor, or Investor Network');
            return;
        }

        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (!formData.ndaAgreed) {
            setError('You must read and agree to the Non-Disclosure Agreement (NDA) to sign up');
            return;
        }

        setLoading(true);

        try {
            const tenantIdentifier = slugifyTenantIdentifier(formData.tenantName);
            const role = userType === 'startup' ? 'STARTUP' : userType === 'investor_network' ? 'INVESTOR_NETWORK' : 'INVESTOR';
            // For startup: only email, password, company name — use company name as display name
            const name = isStartup ? formData.tenantName.trim() : formData.name.trim();
            const payload = {
                name,
                email: formData.email,
                password: formData.password,
                tenantName: formData.tenantName,
                tenantIdentifier,
                role,
                ndaAgreed: true,
                ndaSignatureName: name
            };
            const response = await axios.post(getApiUrl('auth/signup'), payload);

            setSuccess(true);
            // Redirect to /default login after 2 seconds (use server-lowered email so it matches DB / login)
            const loginEmail = response.data?.email || formData.email.trim();
            setTimeout(() => {
                navigate('/default', {
                    replace: true,
                    state: {
                        email: loginEmail,
                        message: 'Account created successfully! Verify your email if required, then sign in.'
                    }
                });
            }, 2000);
        } catch (err) {
            setSuccess(false); // Clear success state
            // Extract detailed error message
            const errorMessage = err.response?.data?.message
                || err.response?.data?.error
                || err.message
                || 'Signup failed. Please try again.';
            setError(errorMessage);
            console.error('Signup error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50">
            {/* Left: Hero images (visible on larger screens) */}
            <div className="hidden lg:flex lg:w-1/2 xl:w-[45%] relative overflow-hidden">
                <div className="absolute inset-0 flex flex-col">
                    <div className="flex-1 min-h-0 relative">
                        <img
                            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                            alt="Team collaboration"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/70 via-transparent to-transparent" />
                    </div>
                    <div className="flex-1 min-h-0 relative">
                        <img
                            src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                            alt="Data and growth"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/60 via-transparent to-transparent" />
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <p className="text-lg font-medium opacity-95">Your Trusted Digital Vault</p>
                    <p className="text-sm opacity-80 mt-1">Secure data rooms for startups and investors</p>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-10">
            <div className="w-full max-w-md">
                <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="px-8 sm:px-10 py-10">
                        <div className="text-center mb-8">
                            <div className="flex justify-center items-center mb-5">
                                <Logo showText={true} width={72} height={72} className="justify-center" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                                Create Account
                            </h2>
                            <p className="text-gray-600 text-sm">
                                Join iVault — a product of Techpuram
                            </p>
                        </div>

                        {!userType ? (
                            <div className="space-y-6">
                                <p className="text-sm text-gray-600 text-center">I am signing up as</p>
                                <div className="grid grid-cols-1 gap-4">
                                    {USER_TYPES.map((type) => (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() => setUserType(type.value)}
                                            className="flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50/50 text-left transition-all"
                                        >
                                            <span className="text-2xl">{type.icon}</span>
                                            <div>
                                                <div className="font-semibold text-gray-900">{type.label}</div>
                                                <div className="text-sm text-gray-500">{type.description}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <div className="text-center pt-2">
                                    <p className="text-sm text-gray-600">
                                        Already have an account?{' '}
                                        <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">Sign in</Link>
                                    </p>
                                </div>
                            </div>
                        ) : (
                        <form className="space-y-6" onSubmit={handleSignup}>
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => setUserType(null)}
                                    className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                    ← Change type
                                </button>
                                <span className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded">
                                    {userType.replace('_', ' ')}
                                </span>
                            </div>
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-sm text-red-800">{error}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setError('')}
                                            className="text-red-400 hover:text-red-600 transition-colors"
                                        >
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {success && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 animate-fade-in">
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <div>
                                            <span className="text-sm font-semibold text-green-800">Account created!</span>
                                            <p className="text-xs text-green-600 mt-1">Check your email to verify your account, then you can log in. Redirecting to login...</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-5">
                                {!isStartup && (
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Full Name
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="John Doe"
                                    />
                                </div>
                                )}

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="you@company.com"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="tenantName" className="block text-sm font-medium text-gray-700 mb-2">
                                        {isStartup ? 'Company Name' : 'Organization Name'}
                                    </label>
                                    <input
                                        id="tenantName"
                                        name="tenantName"
                                        type="text"
                                        required
                                        value={formData.tenantName}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder={isStartup ? 'Your startup name' : 'VC firm, network, or your name'}
                                    />
                                    {isStartup && (
                                        <p className="mt-1 text-xs text-gray-500">You can add more details after your first login.</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                        Password
                                    </label>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password
                                    </label>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 text-gray-900"
                                        placeholder="••••••••"
                                    />
                                </div>

                                {/* NDA */}
                                <div className="pt-4 border-t border-gray-200">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Non-Disclosure Agreement (NDA)</h3>
                                    <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50 max-h-64 overflow-y-auto">
                                        {NDA_DOCUMENT}
                                    </div>
                                    <label className="flex items-start gap-3 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="ndaAgreed"
                                            checked={formData.ndaAgreed}
                                            onChange={(e) => setFormData({ ...formData, ndaAgreed: e.target.checked })}
                                            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">
                                            I have read and agree to the Non-Disclosure Agreement above. I understand that confidential information accessed through iVault must not be disclosed without authorization, and that my agreement is binding as indicated in the NDA.
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Creating Account...' : 'Create Account'}
                            </button>

                            <div className="text-center mt-6">
                                <p className="text-sm text-gray-600">
                                    Already have an account?{' '}
                                    <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                                        Sign in
                                    </Link>
                                </p>
                            </div>
                        </form>
                        )}
                    </div>
                </div>

                <div className="mt-8 text-center">
                    <p className="text-sm text-gray-600">
                        By signing up, you agree to our <Link to="/terms" className="text-blue-600 hover:text-blue-500">Terms of Service</Link>, <Link to="/privacy" className="text-blue-600 hover:text-blue-500">Privacy Policy</Link>, and the NDA above.
                    </p>
                </div>
            </div>
            </div>
        </div>
    );
};

export default Signup;
